import { useMemo, useState } from "react";

import { macroText } from "../domain/foodView";
import { entryFor, totalsOf } from "../domain/intake";
import { readMeal, type LastPortion, type ReadRow } from "../domain/mealText";
import type { FoodEntry, FoodItem, MealSlot } from "../domain/types";
import { orderMatches } from "../lib/foodDb";
import { Button, IconButton } from "./Button";
import { I } from "./icons";
import "./FoodAdd.css";

/**
 * A meal typed as one line in the Add-food box — "2 roti, 1 katori dal, chai" — shown as the rows
 * Kaya read before anything is logged (board FoodAddTyped, SNIPPETS §38; ADR-048).
 *
 * The reading is `domain/mealText.ts`, over the same table and the same exact search the picker
 * uses. Each row can be left out or changed — a change opens the picker on that part's words, and
 * whatever is picked there replaces the reading. A part nothing matched is shown as typed with
 * "Pick a food"; it is never filled with a guess, and it stays out of the sum until it is picked.
 */

export interface TypedRow {
  key: string;
  row: ReadRow;
  /** what will be logged for this part; null while nothing is picked for it */
  entry: FoodEntry | null;
  /** true once picked by hand, which replaces the reading's words */
  picked: boolean;
}

export interface TypedMeal {
  rows: TypedRow[];
  entries: FoodEntry[];
  /** parts with nothing to log yet, in the words typed */
  missing: string[];
  leaveOut: (key: string) => void;
  pick: (key: string, e: FoodEntry) => void;
}

export function useTypedMeal(
  q: string,
  table: FoodItem[] | null,
  last: Map<string, LastPortion>,
  meal: MealSlot
): TypedMeal {
  const [out, setOut] = useState<Record<string, true>>({});
  const [picked, setPicked] = useState<Record<string, FoodEntry>>({});
  const read = useMemo(
    () => (table ? readMeal(q, (x) => orderMatches(table, x), last) : []),
    [q, table, last]
  );
  const rows: TypedRow[] = read
    // keyed by position and words, so editing one part keeps what was done to the others
    .map((row, i) => ({ key: `${i}:${row.part.text}`, row }))
    .filter((r) => !out[r.key])
    .map(({ key, row }) => {
      const hand = picked[key];
      if (hand) return { key, row, entry: { ...hand, meal }, picked: true };
      const entry = row.item ? entryFor(row.item, { qty: row.qty, unit: row.unit, meal }) : null;
      // An emptied portion is no portion, not a zero-calorie food (invariant 11).
      return { key, row, entry: entry && entry.grams > 0 ? entry : null, picked: false };
    });
  const entries = rows.map((r) => r.entry).filter((e): e is FoodEntry => e !== null);
  return {
    rows,
    entries,
    missing: rows.filter((r) => !r.entry).map((r) => r.row.part.name),
    leaveOut: (key) => setOut((x) => ({ ...x, [key]: true })),
    pick: (key, e) => setPicked((x) => ({ ...x, [key]: e })),
  };
}

const n = (v: number) => v.toLocaleString();

/** How the row came to be what it is, after the portion: the second line's reason. */
function why(r: TypedRow, e: FoodEntry): string {
  const portion = `${n(e.qty)} ${e.unit}, ${n(e.grams)} g`;
  if (r.picked) return `${portion} · ${macroText(e)}`;
  if (r.row.unitSwap) return `${portion} · counted in ${r.row.unit}; no ${r.row.unitSwap} for this food`;
  if (r.row.how === "last") return `${portion} · the portion you last logged`;
  if (r.row.how === "recent") return `${portion} · from your recent foods`;
  return `${portion} · ${macroText(e)}`;
}

function andList(xs: string[]): string {
  return xs.length <= 1 ? (xs[0] ?? "") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`;
}

export function TypedMealRows({ meal, onChange }: { meal: TypedMeal; onChange: (key: string, words: string) => void }) {
  const { rows, entries, missing } = meal;
  const t = totalsOf(entries);
  return (
    <div className="k-typed">
      <div className="k-typed__head">
        <h3 className="k-typed__h">What Kaya read</h3>
        <span className="k-typed__count">
          {entries.length} of {rows.length} found
        </span>
      </div>
      <div className="k-rows" role="list">
        {rows.map((r) => {
          const quote = <span className="k-typed__quote">&ldquo;{r.row.part.text}&rdquo;</span>;
          if (!r.entry) {
            return (
              <div key={r.key} role="listitem" className="k-row k-typed__row">
                <span className="k-row__txt">
                  {quote}
                  <span className="k-typed__none">No food by that name yet</span>
                </span>
                <Button
                  variant="ghost"
                  ariaLabel={`Pick a food for ${r.row.part.text}`}
                  onClick={() => onChange(r.key, r.row.part.name)}
                >
                  Pick a food
                </Button>
              </div>
            );
          }
          const e = r.entry;
          return (
            <div key={r.key} role="listitem" className="k-row k-typed__row">
              <button
                type="button"
                className="k-typed__main"
                aria-label={`${e.name}, ${n(e.qty)} ${e.unit}, ${n(e.kcal)} kcal. Change`}
                onClick={() => onChange(r.key, r.row.part.name)}
              >
                {quote}
                <span className="k-typed__line">
                  <span className="k-row__title">{e.name}</span>
                  <span className="k-food-kcal">{n(e.kcal)} kcal</span>
                </span>
                <span className="k-row__sub">{why(r, e)}</span>
              </button>
              <IconButton icon={I.close} label={`Leave out ${r.row.part.text}`} onClick={() => meal.leaveOut(r.key)} />
            </div>
          );
        })}
      </div>
      {entries.length || missing.length ? (
        <p className="k-food-note k-food-fig">
          {entries.length ? `${n(t.kcal)} kcal · ${macroText(t)}` : "Nothing to add yet"}
          {missing.length
            ? ` — ${andList(missing)} ${missing.length === 1 ? "stays" : "stay"} out until you pick ${missing.length === 1 ? "it" : "them"}.`
            : ""}
        </p>
      ) : null}
    </div>
  );
}
